import { useQuery } from "@apollo/client";
import { Box, SxProps, Typography } from "@mui/material";
import { MenuItem } from "@mui/material";
import { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { BasicFormInput } from "@stw/yup-validators";

import {
  ListSubaccountGeneralInboxesQuery,
  ListSubaccountGeneralInboxesQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  RhfSelect,
  RhfSwitchField,
  RhfTextField,
  RhfDurationField,
  RhfTextingHoursFields,
  timeToHHMM,
} from "~frontend/components/forms/react-hook-form-components";
import { LIST_SUBACCOUNT_GENERAL_INBOXES } from "~frontend/graphql/inboxes";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { plural } from "~frontend/util/plural";

import { BasicValues } from "./Basics";

export const TexterSettings = ({
  readOnly,
}: {
  readOnly: boolean;
}): ReactNode => {
  const tenant = useTenant()!;
  const subaccount = useSubaccountAdminConfig()!;

  const generalInboxesEnabled = useFeatureFlag("generalInboxes");

  const listGeneralInboxes = useQuery<
    ListSubaccountGeneralInboxesQuery,
    ListSubaccountGeneralInboxesQueryVariables
  >(LIST_SUBACCOUNT_GENERAL_INBOXES, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  const { getValues, control, watch } = useFormContext<BasicFormInput>();
  const values = getValues();
  const OverrideSwitch = ({
    name,
    label,
    disabled = false,
  }: {
    name: keyof BasicValues;
    label: string;
    disabled?: boolean;
  }) => {
    const value = watch(name);
    return (
      <Box sx={!value ? { pb: 2 } : {}}>
        <RhfSwitchField
          disabled={disabled}
          name={name}
          label={label}
          control={control}
        />
      </Box>
    );
  };

  const OverrideLabel = ({
    name,
    label,
    sx = {},
  }: {
    name: keyof BasicValues;
    label: ReactNode;
    sx?: SxProps;
  }) => {
    const value = watch(name);
    if (value) {
      return null;
    }

    return (
      <Typography variant="h2" sx={{ pt: 1, pb: 0.5, ...sx }}>
        {label}
      </Typography>
    );
  };

  const textingHoursExplanation = (overridden: boolean) => {
    if (overridden) {
      return;
    }
    const formattedTime = (time: string) => {
      const hhmm = timeToHHMM(time);
      const hour = parseInt(hhmm.substring(0, 2), 10);
      const minutes = hhmm.substring(3, 5);
      return `${hour < 13 ? hour : hour - 12}:${minutes} ${
        hour < 12 ? "AM" : "PM"
      }`;
    };
    const timezoneDisplayName = tenant.timezoneOptions.find(
      ({ timezone }) => timezone === subaccount.textingHoursTimezone
    )?.displayName;
    return (
      <>
        Your campaign will use your team's default hours:{" "}
        <strong>
          {formattedTime(subaccount.textingHoursStart)} -{" "}
          {formattedTime(subaccount.textingHoursEnd)} ({timezoneDisplayName})
        </strong>
      </>
    );
  };

  const assignmentDurationLabel = () => {
    if (values.texterAssignmentExpirationMinutes < 60) {
      return plural(
        values.texterAssignmentExpirationMinutes,
        "minute",
        "minutes"
      );
    }

    const hours = Math.floor(values.texterAssignmentExpirationMinutes / 60);
    return plural(hours, "hour", "hours");
  };

  return (
    <FlexBox direction="column">
      <Typography variant="h0Bold" sx={{ pb: 1, pt: 3 }} component="div">
        Texter Settings
      </Typography>

      {generalInboxesEnabled ? (
        <>
          <OverrideLabel
            name="inboxOverride"
            label="Your campaign will use texter links to invite texters to claim
          replies. This cannot be changed once the campaign has started."
            sx={{ pt: 0 }}
          />

          <OverrideSwitch
            disabled={readOnly}
            name="inboxOverride"
            label="Route replies to an inbox. This cannot be changed once the
          Campaign has started."
          />

          {values.inboxOverride ? (
            <AsyncWrapper
              data={listGeneralInboxes.data}
              errors={listGeneralInboxes.errors}
              loading={listGeneralInboxes.loading}
            >
              {data => (
                <RhfSelect
                  disabled={readOnly}
                  name="inboxId"
                  label="Inbox"
                  control={control}
                >
                  {data.listSubaccountGeneralInboxes.map(({ name, id }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </RhfSelect>
              )}
            </AsyncWrapper>
          ) : null}
        </>
      ) : undefined}
      <OverrideLabel
        name="textingHoursOverrideSubaccount"
        label={textingHoursExplanation(false)}
        sx={{ pt: 0 }}
      />

      <OverrideSwitch
        name="textingHoursOverrideSubaccount"
        label="Override default texting hours"
      />
      {values.textingHoursOverrideSubaccount && <RhfTextingHoursFields />}

      {!values.inboxOverride ? (
        <>
          <OverrideLabel
            name="texterAssignmentBatchSizeOverrideSubaccount"
            label={
              <>
                Your campaign will use your team's default assignment batch
                size: <strong>{subaccount.texterAssignmentBatchSize}</strong>
              </>
            }
          />

          <OverrideSwitch
            name="texterAssignmentBatchSizeOverrideSubaccount"
            label="Override default batch size"
          />

          {values.texterAssignmentBatchSizeOverrideSubaccount && (
            <RhfTextField
              name="texterAssignmentBatchSize"
              label="Texter Assignment Batch Size"
              type="number"
              bottomLabel="The number of replies that will be assigned to a texter at a time"
              control={control}
              sx={{ flex: 1 }}
            />
          )}

          <OverrideLabel
            name="texterAssignmentExpirationMinutesOverrideSubaccount"
            label={
              <>
                Your campaign will use your team's default assignment duration:{" "}
                <strong>{assignmentDurationLabel()}</strong>
              </>
            }
          />

          <OverrideSwitch
            name="texterAssignmentExpirationMinutesOverrideSubaccount"
            label="Override default assignment duration"
          />

          {values.texterAssignmentExpirationMinutesOverrideSubaccount && (
            <RhfDurationField
              name="texterAssignmentExpirationMinutes"
              label="Assignment Duration"
              bottomLabel="After this amount of time, assignments will go back into the pool for reassignment"
              fullWidth
              control={control}
            />
          )}
        </>
      ) : undefined}
    </FlexBox>
  );
};
